import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import "./AddAutovehicles.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Select from 'react-select';
import { NumericFormat } from "react-number-format";

const ContractInformation = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // opțiuni pentru status - mutat în useMemo
    const statusOptions = useMemo(() => ["Open", "Closed"], []);

    const [loading, setLoading] = useState(true);
    const [suppliers, setSuppliers] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // State pentru validare
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [contractFieldErrors, setContractFieldErrors] = useState({});

    // date pentru contract - CU EXPIRE ADAUGAT
    const initialContractData = useMemo(() => ({
        supplier: "",
        signing_date: "",
        signing_date_display: "",
        duration: "",
        start_date: "",
        start_date_display: "",
        expire: "",
        expire_display: "",
        status: "Open",
        advance_payment: "",
        monthly_installment: "",
        value: "",
        annual_mileage: "",
        replacement_days: "",
        km_pickup: "",
        km_return: "",
    }), []);

    const [contractData, setContractData] = useState(initialContractData);
    const [originalContractData, setOriginalContractData] = useState(null);

    // Stiluri personalizate pentru react-select (la fel ca în EditAutovehicles)
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: '100%',
            height: '38px',
            border: state.isFocused ? '2px solid #050d15' : '1.5px solid #ccc',
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(0, 0, 0, 0.2)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#050d15' : '1.5px solid #ccc',
            },
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            minHeight: '38px',
            cursor: 'pointer',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#777',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#1a1a1a',
            fontFamily: "'Arial', sans-serif",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '36px',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#1976d2' : '#555',
            padding: '8px',
            '&:hover': {
                color: '#1976d2',
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 9999,
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px 0',
            maxHeight: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#1976d2' :
                state.isFocused ? '#f0f7ff' : 'white',
            color: state.isSelected ? 'white' : '#1a1a1a',
            padding: '8px 12px',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            cursor: 'pointer',
            '&:active': {
                backgroundColor: '#1976d2',
                color: 'white'
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#666',
            padding: '8px',
            '&:hover': {
                color: '#333'
            }
        }),
    };

    // Stil pentru select-uri cu eroare
    const errorSelectStyles = {
        ...customSelectStyles,
        control: (provided, state) => ({
            ...customSelectStyles.control(provided, state),
            border: '1px solid #ff5c5c !important',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 92, 92, 0.2)' : '0 0 0 1px rgba(255, 92, 92, 0.5)',
            '&:hover': {
                borderColor: '#ff5c5c !important',
            }
        })
    };

    // Opțiuni pentru react-select
    const supplierOptions = useMemo(() =>
        suppliers.map(supplier => ({
            value: supplier.id,
            label: supplier.name
        })), [suppliers]);

    const statusSelectOptions = useMemo(() =>
        statusOptions.map(status => ({
            value: status,
            label: status
        })), [statusOptions]);

    // Handler pentru react-select pentru supplier
    const handleSupplierSelect = (selectedOption) => {
        if (!editMode) return;

        setContractData(prev => ({
            ...prev,
            supplier: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, supplier: "" }));
        }
    };

    // Handler pentru react-select pentru status
    const handleStatusSelect = (selectedOption) => {
        if (!editMode) return;

        setContractData(prev => ({
            ...prev,
            status: selectedOption ? selectedOption.value : "Open"
        }));
    };

    // Handler special pentru NumericFormat câmpuri
    const handleNumericChange = (field) => ({ value }) => {
        if (!editMode) return;
        
        setContractData((prev) => ({ ...prev, [field]: value }));
        
        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // Găsește valoarea selectată pentru supplier și status
    const selectedSupplier = supplierOptions.find(option => option.value === contractData.supplier);
    const selectedStatus = statusSelectOptions.find(option => option.value === contractData.status);

    // Validare contract form (la fel ca în EditAutovehicles)
    const validateContractForm = () => {
        const errors = {};

        const requiredContractFields = [
            "supplier",
            "signing_date",
            "duration",
            "start_date",
            "expire",
            "advance_payment",
            "monthly_installment",
            "value",
            "annual_mileage",
            "replacement_days",
            "km_pickup",
            "km_return",
        ];

        requiredContractFields.forEach((f) => {
            const value = contractData[f];

            if (f === "advance_payment" || f === "monthly_installment" || f === "value") {
                // Convertim valoarea formatată pentru validare
                const numericValue = value ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : null;
                if (!value || value.toString().trim() === "" || numericValue <= 0) {
                    errors[f] = "Required";
                }
            } else if (f === "annual_mileage" || f === "replacement_days" ||
                f === "km_pickup" || f === "km_return" || f === "duration") {
                if (!value || value.toString().trim() === "" || parseInt(value) < 0) {
                    errors[f] = "Required";
                }
            } else if (!value || value.toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        return errors;
    };

    // Funcții pentru handleChange
    const handleContractDatePick = (field, date) => {
        if (!editMode) return;

        if (!date) {
            setContractData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            if (isSubmitted && field === "expire") {
                setContractFieldErrors(prev => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setContractData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const handleContractChange = (field) => (e) => {
        if (!editMode) return;

        let value = e.target.value;

        if (field === "annual_mileage" || field === "replacement_days" ||
            field === "km_pickup" || field === "km_return" || field === "duration") {
            value = value.replace(/\D/g, "");
        }

        setContractData((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // Funcții ajutătoare
    const cleanPayload = (obj) => {
        const out = {};
        for (const key in obj) {
            out[key] = obj[key] === "" ? null : obj[key];
        }
        return out;
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
    };

    const shouldShowError = (fieldName, errorObj) => {
        return isSubmitted && errorObj[fieldName];
    };

    // Permisiuni
    const [canView, setCanView] = useState(false);
    const [canEditPermission, setCanEditPermission] = useState(false);
    const [canCreate, setCanCreate] = useState(false);

    useEffect(() => {
        const requiredPath = "/all-autovehicles";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const pages = storedPermissions?.pages ?? [];

        const checkPermission = (key) =>
            pages.some((p) => {
                try {
                    return new URL(p.url).pathname === requiredPath && p[key] === true;
                } catch {
                    return false;
                }
            });

        setCanView(checkPermission("can_view"));
        setCanEditPermission(checkPermission("can_edit"));
        setCanCreate(checkPermission("can_create"));
    }, []);

    // încarcă datele contractului
    useEffect(() => {
        const load = async () => {
            try {
                // Mai întâi obținem departamentele permise
                const allowedDeptsResponse = await axiosInstance.get("/department/user/");
                const allowedDeptIds = allowedDeptsResponse.data.map(d => d.value);

                // Apoi obținem toate datele în paralel
                const [autoResponse, suppliersResponse] = await Promise.all([
                    axiosInstance.get(`/all-autovehicles/${id}/`),
                    axiosInstance.get("/suppliers/")
                ]);

                // Filtrăm supplierii pe baza departamentelor permise
                let filteredSuppliers = suppliersResponse.data;
                if (allowedDeptIds.length > 0) {
                    filteredSuppliers = filteredSuppliers.filter(supplier =>
                        supplier.departments?.some(deptId => allowedDeptIds.includes(deptId))
                    );
                }

                const d = autoResponse.data;
                const vehicleSupplierId = d.supplier;

                // ADĂUGAT: Dacă supplier-ul vehiculului nu este în lista filtrați, îl adăugăm forțat
                if (vehicleSupplierId) {
                    const supplierInList = filteredSuppliers.find(s => s.id === vehicleSupplierId);
                    if (!supplierInList) {
                        // Găsim supplier-ul în lista completă și îl adăugăm la lista filtrați
                        const fullSupplier = suppliersResponse.data.find(s => s.id === vehicleSupplierId);
                        if (fullSupplier) {
                            filteredSuppliers = [...filteredSuppliers, fullSupplier];
                        }
                    }
                }

                setSuppliers(filteredSuppliers);

                // Formatăm datele contractului
                const contractFormatted = {
                    supplier: d.supplier || "",
                    signing_date: d.signing_date || "",
                    signing_date_display: d.signing_date ? format(new Date(d.signing_date), "dd.MM.yyyy") : "",
                    duration: d.duration || "",
                    start_date: d.start_date || "",
                    start_date_display: d.start_date ? format(new Date(d.start_date), "dd.MM.yyyy") : "",
                    expire: d.expire || "",
                    expire_display: d.expire ? format(new Date(d.expire), "dd.MM.yyyy") : "",
                    status: d.status || "Open",
                    advance_payment: d.advance_payment || "",
                    monthly_installment: d.monthly_installment || "",
                    value: d.value || "",
                    annual_mileage: d.annual_mileage || "",
                    replacement_days: d.replacement_days || "",
                    km_pickup: d.km_pickup || "",
                    km_return: d.km_return || "",
                };

                setContractData(contractFormatted);
                setOriginalContractData(contractFormatted);

                console.log("Contract data loaded successfully");
                console.log("Filtered suppliers count:", filteredSuppliers.length);
                console.log("Vehicle supplier ID:", vehicleSupplierId);

            } catch (e) {
                console.error("LOAD ERROR", e);
                console.error("Error details:", e.response?.data);
                showError("Error loading contract data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [id]);

    // salvare modificări
    const handleSave = async () => {
        if (!editMode) return;

        setIsSubmitted(true);

        const contractErrors = validateContractForm();
        setContractFieldErrors(contractErrors);

        const hasErrors = Object.keys(contractErrors).length > 0;

        if (hasErrors) {
            showError("Please fill in all required fields");
            return;
        }

        if (!canEditPermission && !canCreate) return;

        setIsSaving(true);

        try {
            await axiosInstance.put(`/all-autovehicles/${id}/`, cleanPayload({
                supplier: contractData.supplier ? parseInt(contractData.supplier) : null,
                signing_date: contractData.signing_date || null,
                duration: contractData.duration || null,
                start_date: contractData.start_date || null,
                expire: contractData.expire || null,
                status: contractData.status,
                advance_payment: contractData.advance_payment ? 
                    parseFloat(contractData.advance_payment.replace(/\./g, '').replace(',', '.')) : null,
                monthly_installment: contractData.monthly_installment ? 
                    parseFloat(contractData.monthly_installment.replace(/\./g, '').replace(',', '.')) : null,
                value: contractData.value ? 
                    parseFloat(contractData.value.replace(/\./g, '').replace(',', '.')) : null,
                annual_mileage: contractData.annual_mileage ? parseInt(contractData.annual_mileage) : null,
                replacement_days: contractData.replacement_days ? parseInt(contractData.replacement_days) : null,
                km_pickup: contractData.km_pickup ? parseInt(contractData.km_pickup) : null,
                km_return: contractData.km_return ? parseInt(contractData.km_return) : null,
            }));

            setOriginalContractData(contractData);
            setEditMode(false);
            setIsSaving(false);
            setIsSubmitted(false);

            setShowToast(true);
            setTimeout(() => setShowToast(false), 1500);

        } catch (err) {
            console.error("Save error:", err);
            setIsSaving(false);
            showError("Error saving contract. Please check the data and try again.");
        }
    };

    // revine la valorile originale
    const handleCancel = () => {
        setContractData(originalContractData);
        setEditMode(false);
        setContractFieldErrors({});
        setIsSubmitted(false);
    };

    if (loading) return <div className="loading">Loading...</div>;

    if (!canView) {
        return (
            <div style={{ padding: "40px", textAlign: "center", fontSize: "18px" }}>
                ❌ You do not have permission to view this page.
            </div>
        );
    }

    return (
        <>
            {showToast && <div className="global-toast success">✔ Contract updated successfully!</div>}
            {showErrorToast && <div className="global-toast error">✗ {errorMessage}</div>}

            <div className="addautovehicles-page">
                <div className="add-autovehicle-container">

                    {/* HEADER - EXACT CA ÎN EDIT */}
                    <div className="top-header addauto-header">
                        <div className="header-left">
                            <ArrowBackIcon className="back-icon-autovehicles" onClick={() => navigate("/all-autovehicles")} />
                            <h2 className="header-title-autovehicles">View autovehicle</h2>
                        </div>

                        <div className="header-actions">
                            {!editMode ? (
                                canEditPermission && (
                                    <button className="edit-btn" onClick={() => setEditMode(true)}>
                                        <EditIcon style={{ fontSize: "18px" }} />
                                        EDIT
                                    </button>
                                )
                            ) : (
                                <>
                                    <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                                    <button
                                        className={`save-btn ${isSaving ? "disabled" : ""}`}
                                        onClick={!isSaving ? handleSave : undefined}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <hr className="header-divider-autovehicles" />

                    {/* TABS */}
                    <div className="tab-buttons-autovehicles">
                        <button
                            className={`tab-btn ${window.location.pathname === `/all-autovehicles/view/${id}` ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}`)}
                        >
                            Details
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/contract-information') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/contract-information`)}
                        >
                            Contract Information
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/mileagehistory') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/mileagehistory`)}
                        >
                            Mileage History
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/problems') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/problems`)}
                        >
                            Problems
                        </button>
                    </div>

                    {/* CONTRACT FORM - EXACT CA ÎN EDIT AUTOVEHICLES */}
                    <div className="contract-form-section">

                        <div className="contract-form-grid">
                            {/* ROW 1 - SUPPLIER FULL WIDTH */}
                            <div className="contract-row-full">
                                <div className="field full-width">
                                    <label>Supplier <span className="required">*</span></label>
                                    <Select
                                        value={selectedSupplier}
                                        onChange={handleSupplierSelect}
                                        options={supplierOptions}
                                        placeholder="Select supplier"
                                        isSearchable
                                        isClearable={true}
                                        styles={shouldShowError("supplier", contractFieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("supplier", contractFieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                        isDisabled={!editMode}
                                    />
                                    {shouldShowError("supplier", contractFieldErrors) && <span className="field-error">{contractFieldErrors.supplier}</span>}
                                </div>
                            </div>

                            {/* ROW 2 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Signing Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-signing"
                                            selected={contractData.signing_date ? new Date(contractData.signing_date) : null}
                                            onChange={(date) => handleContractDatePick("signing_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("signing_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-signing")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("signing_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.signing_date}</span>}
                                </div>

                                <div className="field">
                                    <label>Duration <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={contractData.duration}
                                        placeholder="Ex: 36 months"
                                        onChange={handleContractChange("duration")}
                                        className={shouldShowError("duration", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("duration", contractFieldErrors) && <span className="field-error">{contractFieldErrors.duration}</span>}
                                </div>

                                <div className="field">
                                    <label>Start Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-start"
                                            selected={contractData.start_date ? new Date(contractData.start_date) : null}
                                            onChange={(date) => handleContractDatePick("start_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("start_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-start")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("start_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.start_date}</span>}
                                </div>
                            </div>

                            {/* ROW 3 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Expire Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-expire"
                                            selected={contractData.expire ? new Date(contractData.expire) : null}
                                            onChange={(date) => handleContractDatePick("expire", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("expire", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-expire")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("expire", contractFieldErrors) && <span className="field-error">{contractFieldErrors.expire}</span>}
                                </div>

                                <div className="field">
                                    <label>Status <span className="required">*</span></label>
                                    <Select
                                        value={selectedStatus}
                                        onChange={handleStatusSelect}
                                        options={statusSelectOptions}
                                        placeholder="Select status"
                                        isSearchable
                                        styles={customSelectStyles}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={!editMode}
                                    />
                                </div>

                                <div className="field">
                                    <label>Advance Payment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.advance_payment}
                                        onValueChange={handleNumericChange("advance_payment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("advance_payment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                        disabled={!editMode}
                                    />
                                    {shouldShowError("advance_payment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.advance_payment}</span>}
                                </div>
                            </div>

                            {/* ROW 4 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Monthly Installment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.monthly_installment}
                                        onValueChange={handleNumericChange("monthly_installment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("monthly_installment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                        disabled={!editMode}
                                    />
                                    {shouldShowError("monthly_installment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.monthly_installment}</span>}
                                </div>

                                <div className="field">
                                    <label>Contract Value (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.value}
                                        onValueChange={handleNumericChange("value")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("value", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                        disabled={!editMode}
                                    />
                                    {shouldShowError("value", contractFieldErrors) && <span className="field-error">{contractFieldErrors.value}</span>}
                                </div>

                                <div className="field">
                                    <label>Annual Km <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={contractData.annual_mileage}
                                        placeholder="0"
                                        onChange={handleContractChange("annual_mileage")}
                                        className={shouldShowError("annual_mileage", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("annual_mileage", contractFieldErrors) && <span className="field-error">{contractFieldErrors.annual_mileage}</span>}
                                </div>
                            </div>

                            {/* ROW 5 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Replacement Days <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={contractData.replacement_days}
                                        placeholder="0"
                                        onChange={handleContractChange("replacement_days")}
                                        className={shouldShowError("replacement_days", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("replacement_days", contractFieldErrors) && <span className="field-error">{contractFieldErrors.replacement_days}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Pickup <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={contractData.km_pickup}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_pickup")}
                                        className={shouldShowError("km_pickup", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_pickup", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_pickup}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Return <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={contractData.km_return}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_return")}
                                        className={shouldShowError("km_return", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_return", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_return}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default ContractInformation;